import React, { act, useContext, useState } from 'react';
import ContentBox from 'Components/common/ContentBox/ContentBox';
import DefaultLayout from 'Components/DefaultLayout/DefaultLayout';
import ButtonGenerator from './ButtonGenerator/ButtonGenerator';
import Space from 'Components/common/Space/Space';
import { Table, Button, Row, Col, Modal, Select } from 'antd';
import { CopyOutlined, EyeOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import useMarketingHook from './Marketing.hook';
import styles from './Marketing.styles';
import { copyTextToClipboard } from 'utils/TextUtils';
import { AppContext } from 'App.context';
import LinkGenerator from './LinkGenerator/LinkGenerator';
import InfoView from 'Components/common/InfoView/InfoView';
import { InfoCircleFilled } from '@ant-design/icons';
import Colors from 'constants/Colors';
import AppTextInput from 'Components/common/AppTextInput/AppTextInput';
import AppButton from 'Components/common/Forms/AppButton/AppButton';
import Loading from 'Components/common/Loader/Loading';

export default function Marketing() {
  const {
    appActions: { translate },
  } = useContext(AppContext);
  const { state, actions } = useMarketingHook();

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
    },
    {
      title: 'Copy',
      dataIndex: 'copy',
      key: 'copy',
      render: (_, row) => {
        return (
          <div>
            <Button
              type="primary"
              shape="square"
              ghost
              style={{ color: Colors.superUserButton, borderColor: Colors.lightGray }}
              icon={<CopyOutlined />}
              onClick={() => copyTextToClipboard(row.url)}
            />
          </div>
        );
      },
    },
  ];

    const selectDateOptions = [
      { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
      { value: 'YYYY/MM/DD', label: 'YYYY/MM/DD' },
      { value: 'MM-DD-YYYY', label: 'MM-DD-YYYY' },
      { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
      { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY' },
    ];

  return (
    <DefaultLayout>
      <Modal
        open={state.showChangeSlugModal}
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
            {translate('marketing.changeSlugTitle')}
          </span>
        }
        cont
        okText="Guardar"
        okButtonProps={{
          disabled: state.showChangeSlugModalLoading,
        }}
        onOk={() => {
          actions.updateSlug();
        }}
        cancelText="Cancelar"
        cancelButtonProps={{
          disabled: state.showChangeSlugModalLoading,
        }}
        onCancel={() => actions.toggleChangeSlugModal()}
      >
        {state.showChangeSlugModalLoading && (
          <Row>
            <Col align={'middle'} span={24}>
              <Loading />
            </Col>
          </Row>
        )}

        {state.showChangeSlugModalLoading === false && (
          <Row>
            <Col span={24}>
              <p>{translate('marketing.changeSlugWarning')}</p>
            </Col>
          </Row>
        )}
      </Modal>

      <ContentBox noPadding title={translate('marketing.buttonGenerator')}>
        <ButtonGenerator
          title={translate('marketing.hotelCheckin')}
          url={`https://checkin.civitfun.com/hotel/${actions.getSelectedHotelSlug()}`}
        />
        <Space height={48} />
        <ButtonGenerator
          title={translate('marketing.chainCheckin')}
          url={`https://checkin.civitfun.com/chain/${actions.getChainSlug()}`}
        />
      </ContentBox>

      <ContentBox title={translate('marketing.changeSlug')} noPadding>
        <Row align={'middle'} style={{ padding: '0 0 1rem 0' }}>
          <Col span={24}>
            <span style={styles.desc}>{`${translate('marketing.changeSlugDesc')}`}</span>
          </Col>
        </Row>
        <Row align={'middle'} justify="space-between">
          <Col span={19}>
            <AppTextInput
              name={translate('marketing.hotelSlug')}
              value={state.hotelSlugToBeChanged}
              onChange={text => actions.onHotelSlugChanged(text)}
            />
          </Col>
          <Col span={4}>
            <AppButton
              disabled={state.changeHotelSlugErrorMessage}
              text={translate('save')}
              action={() => actions.openChangeSlugModal(true)}
            />
          </Col>
        </Row>

        {state.changeHotelSlugErrorMessage && (
          <Row>
            <Col span={24} style={styles.error}>
              {state.changeHotelSlugErrorMessage}
            </Col>
          </Row>
        )}

        <Row align={'middle'} justify="space-between" style={{ marginTop: 20 }}>
          <Col span={19}>
            <AppTextInput
              name={translate('marketing.chainSlug')}
              value={state.chainSlugToBeChanged}
              onChange={text => actions.onChainSlugChanged(text)}
            />
          </Col>
          <Col span={4}>
            <AppButton
              disabled={state.changeChainSlugErrorMessage}
              text={translate('save')}
              action={() => actions.openChangeSlugModal(false)}
            />
          </Col>
        </Row>

        {state.changeChainSlugErrorMessage && (
          <Row align={'top'}>
            <Col span={24} style={styles.error}>
              {state.changeChainSlugErrorMessage}
            </Col>
          </Row>
        )}
      </ContentBox>

      <ContentBox title={translate('marketing.links')} noPadding>
        <Table dataSource={state.data} columns={columns} pagination={{ defaultPageSize: 5 }} />
      </ContentBox>

      <ContentBox title={translate('marketing.deeplink')}>
        <div style={styles.desc}>{`${translate('marketing.deeplink1desc')}`}</div>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
          <div style={{ marginRight: '2%' }}>
            <LinkGenerator url={actions.getHotelDeeplink()} />
          </div>
          <Button
            type="primary"
            shape="square"
            ghost
            icon={<EyeOutlined />}
            style={{
              color: Colors.superUserButton,
              borderColor: Colors.lightGray,
              alignSelf: 'center',
              marginTop: 8,
              marginBottom: 16,
              gap: 16,
              width: 40,
            }}
            onClick={() => actions.redirectToDeeplinkBooking()}
          />
        </div>

        <InfoView style={styles.info} icon={<InfoCircleFilled />}>
          <div style={styles.infoTitle}>{translate('marketing.bookingCodeTitle')}</div>
          <div>{translate('marketing.bookingCodeDesc')}</div>
          <div style={{ height: 16 }} />
          <div style={styles.infoTitle}>{translate('marketing.entryDateTitle')}</div>
          <div>{translate('marketing.entryDateDesc')}</div>
        </InfoView>
        
         <Row align={'middle'} justify="space-between" style={{ marginTop: 20 }}>
          <div style={{ marginTop: '2%'}}>{translate('marketing.acceptedEntranceDate')}</div>
          <Col span={19}>
            <Select
              style={{ width: "100%"}}
              options={selectDateOptions}
              value={state.selectedEntranceDateFormat || "YYYY-MM-DD"}
              onChange={(value) => actions.setSelectedValue('selectedEntranceDateFormat', value)}
            />
          </Col>
          <Col span={4}>
            <AppButton
              text={translate('save')}
              action={() => actions.updateEntranceDateFormat()}
            />
          </Col>
        </Row>
        
      </ContentBox>

      {/* //Aqui empieza el content box de booking */}
      <ContentBox title={<>{translate('marketing.deeplinkBooking')}</>}>
        <img
          style={{ height: '22px', width: '110px' }}
          src={require('assets/images/ota/booking.png')}
        />
        <div style={styles.desc}>{`${translate('marketing.deeplink1descBooking')}`}</div>
        <LinkGenerator url={actions.getHotelDeeplinkBooking()} />

        <InfoView style={styles.info} icon={<InfoCircleFilled />}>
          <div style={styles.infoTitle}>{translate('marketing.bookingCodeTitleBooking')}</div>
          <div>
            {' '}
            <a
              href="https://academy.civitfun.com/hc/es/articles/18859415118237-Sending-your-Civitfun-Check-in-Link-to-Guests-via-Booking-com-Messages"
              target="_blank"
              style={{ textDecoration: 'none' }}
            >
              {translate('marketing.bookingCodeDescBookingLink')}
            </a>
            {translate('marketing.bookingCodeDescBooking')}
          </div>
          <div style={{ height: 16 }} />
        </InfoView>
      </ContentBox>
    </DefaultLayout>
  );
}
